import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useSelector } from "react-redux";
import {
  bookletJobStageIDSelector,
  inspectorsSelector,
  selectedBookletTokenSelector,
  setSelectedBookletToken,
  setSelectedInteractorJobStageID,
} from "../../redux/rhapsodyReducer";
import { useDispatch } from "react-redux";
import DialogClose from "../DialogClose/dialogClose";
import {
  useGetPublicBookletQuery,
  useUpdateBookletMutation,
} from "../../redux/booklet";
import { Box, Button, Chip, Divider, Sheet, Typography } from "@mui/joy";
import { useEffect, useRef, useState, useMemo } from "react";
import SignatureCanvas from "react-signature-canvas";
import parse from "html-react-parser";
import { useAskQuestion } from "../askQuestion/askQuestion";
import { useMissionMap } from "../../templates/projectInvite";
import { useSearchParams } from "react-router-dom";
import { Inspector } from "../../entities/missionMap/missionMap";
import { useAnswerMutation } from "../../redux/missionMap";
import moment from "moment";
import { getContrastYIQ } from "../../helpers";
import { useConfettiCenter } from "../../context/confettiCenter/confettiCenter";
import { OnboardingRequiredDialog } from "../../routes/onboardingRequired";

// Smooth scrolling helpers
function findScrollableAncestor(el: HTMLElement): HTMLElement | Window {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return window;
}

function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

function smoothScrollTo(
  container: HTMLElement | Window,
  to: number,
  duration = 600,
): Promise<void> {
  const start =
    container === window
      ? window.scrollY
      : (container as HTMLElement).scrollTop;
  const change = to - start;
  const startTime = performance.now();

  return new Promise((resolve) => {
    function animate(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeInOutQuad(progress);
      const current = start + change * eased;

      if (container === window) {
        window.scrollTo(0, current);
      } else {
        (container as HTMLElement).scrollTop = current;
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve();
      }
    }
    requestAnimationFrame(animate);
  });
}

async function smoothScrollIntoViewCenter(
  el: HTMLElement,
  duration = 600,
): Promise<void> {
  const container = findScrollableAncestor(el);
  if (container === window) {
    const rect = el.getBoundingClientRect();
    const target =
      window.scrollY + rect.top - (window.innerHeight / 2 - rect.height / 2);
    await smoothScrollTo(window, target, duration);
  } else {
    const c = container as HTMLElement;
    const elRect = el.getBoundingClientRect();
    const cRect = c.getBoundingClientRect();
    const current = c.scrollTop;
    const offsetTop = elRect.top - cRect.top;
    const target = current + offsetTop - (cRect.height / 2 - elRect.height / 2);
    await smoothScrollTo(c, target, duration);
  }
}

function printPortrait(): void {
  // Inject a print stylesheet to force portrait orientation and only print the contract content
  const style = document.createElement("style");
  style.setAttribute("id", "print-portrait-style");
  style.setAttribute("media", "print");
  style.innerHTML = `
    @page { size: Letter portrait; margin: 12mm; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      /* Only show the printable content */
      body * { visibility: hidden !important; }
      #printableDiv, #printableDiv * { visibility: visible !important; }
      #printableDiv { position: absolute; left: 0; top: 0; width: 100%; }
    }
  `;
  document.head.appendChild(style);

  // Trigger the native print dialog
  window.print();

  // Cleanup after print
  setTimeout(() => {
    style.remove();
  }, 1000);
}

/**
 *
 * @returns {ReactElement} SignBooklet page
 */
export function SignBooklet() {
  const bookletToken = useSelector(selectedBookletTokenSelector);
  const { data } = useGetPublicBookletQuery(bookletToken!);
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");
  const mission = useMissionMap(token!);
  const dispatch = useDispatch();
  const [signOpen, setSignOpen] = useState(false);
  const [onboardingDialogOpen, setOnboardingDialogOpen] = useState(false);
  const [signature, setSignature] = useState<string | null>();
  const booklet = data?.booklet;
  const inspectors = useSelector(inspectorsSelector);
  const [signatureCount, setSignatureCount] = useState(0);
  const [signedSlots, setSignedSlots] = useState<Record<number, boolean>>({});
  const [totalSlots, setTotalSlots] = useState(0);
  const [finalizedContent, setFinalizedContent] = useState<string>("");
  const askQuestion = useAskQuestion();
  const [signatureDate] = useState<Date>(() => new Date());
  const [updateBooklet] = useUpdateBookletMutation();
  const [answer] = useAnswerMutation();
  const messages = mission?.allMessages();
  const jobStageID = messages?.find((m) => m.Name === "Contract")?.JobStageID;
  const confettiCenter = useConfettiCenter();
  const formattedSignatureDate = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      }).format(signatureDate),
    [signatureDate],
  );

  const selectedMessage = messages?.find((m) => m.JobStageID === jobStageID);

  const highlightRef = useRef<HTMLElement | null>(null);
  const highlightTimer = useRef<number | null>(null);

  useEffect(() => {
    if (booklet?.content) {
      const count = (booklet.content.match(/\{\{musicianSignature\}\}/g) || [])
        .length;
      setTotalSlots(count);
    } else {
      setTotalSlots(0);
    }
  }, [booklet?.content]);

  useEffect(() => {
    const remaining = Math.max(totalSlots - Object.keys(signedSlots).length, 0);
    setSignatureCount(remaining);
  }, [totalSlots, signedSlots]);

  const addHighlight = (el: HTMLElement) => {
    if (highlightRef.current) {
      highlightRef.current.classList.remove("sig-highlight");
    }
    highlightRef.current = el;
    el.classList.add("sig-highlight");
    if (highlightTimer.current) {
      window.clearTimeout(highlightTimer.current);
    }
    highlightTimer.current = window.setTimeout(() => {
      el.classList.remove("sig-highlight");
      if (highlightRef.current === el) highlightRef.current = null;
    }, 2400);
  };

  const prepareInspectors = async () => {
    const ret: Inspector[] = [];
    for (const key in inspectors) {
      if (Object.hasOwn(inspectors, key)) {
        const inspector = inspectors[key];
        ret.push({ ...inspector, message: `${inspector.message}` });
      }
    }

    return ret;
  };

  const answerAll = async () => {
    const body: {
      [token: string]: {
        mercuryInteractorID?: number;
        jobStageID?: number;
        mercuryInspectors?: Inspector[];
      };
    } = {};

    const contractInteractors = mission?.contractInteractors();

    const ins = await prepareInspectors();

    contractInteractors?.forEach((s) => {
      body[s.token!] = {
        mercuryInteractorID: s.ID,
        mercuryInspectors: [],
      };
    });
    await answer(body).unwrap();
  };

  const jumpToNextSignature = (delay?: number) => {
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>(
        "[data-sig-slot]:not([data-signed])",
      ),
    );
    if (!nodes.length) return;

    // Prefer the first unsigned slot that is at or below the current viewport.
    const next =
      nodes.find((el) => el.getBoundingClientRect().top >= 0) || nodes[0];

    const doScroll = async () => {
      await smoothScrollIntoViewCenter(next, 650);
      next.focus();
      addHighlight(next);
    };

    if (typeof delay === "number" && delay > 0) {
      window.setTimeout(doScroll, delay);
    } else {
      void doScroll();
    }
  };

  const handleApplySignature = (idx: number) => {
    if (mission?.onboardingRequired()) {
      setOnboardingDialogOpen(true);
      return;
    }
    if (!signature) {
      setSignOpen(true);
      return;
    }
    const el = document.querySelector<HTMLElement>(
      `[data-sig-slot][data-idx="${idx}"]`,
    );
    if (el) addHighlight(el);
    setSignedSlots((prev) => ({ ...prev, [idx]: true }));
    // After marking this slot as signed, scroll to the next unsigned slot
    // Use two rAFs to ensure the DOM reflects the updated `data-signed` attributes
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        jumpToNextSignature(400);
      });
    });
  };

  const buildSignatureImgTag = () => {
    if (!signature) return "";
    // inline style to keep layout similar to the slot box
    return `<img src="data:image/png;base64,${signature}" alt="Signature" style="max-height:56px; max-width:300px;" />`;
  };

  const handleSubmit = async () => {
    if (mission?.onboardingRequired()) {
      setOnboardingDialogOpen(true);
      return;
    }
    if (!booklet?.content || !signature) return;
    // Replace every placeholder with the adopted signature image
    const updated = booklet.content
      .replace(/\{\{musicianSignature\}\}/g, buildSignatureImgTag())
      .replace(/\{\{musicianSignatureDate\}\}/g, formattedSignatureDate);
    setFinalizedContent(updated);
    dispatch(setSelectedInteractorJobStageID(jobStageID));
    try {
      await updateBooklet({
        token: bookletToken!,
        body: {
          "{{musicianSignature}}": signature,
          "{{musicianSignatureDate}}": `${
            mission?.MusicianName
          } on ${moment().format("lll")}`,
        },
      }).unwrap();
      await answerAll();
    } catch (e) {
      askQuestion("Something went wrong", ["OK"], {
        subtitle: (
          <Typography>
            We could not submit your signed contract. Please check your
            connection and try again.
          </Typography>
        ),
      });
      return;
    }
    askQuestion("Do you want to save a copy?", ["No", "Yes"], {
      subtitle: (
        <Typography>
          Your contract will be included in all communications related to this
          season moving forward.
        </Typography>
      ),
    }).then((i) => {
      askQuestion("You are all set!", ["OK"], {
        subtitle: (
          <Typography>
            Your contract has been signed and submitted successfully.
          </Typography>
        ),
      });
      confettiCenter.show();
      if (i == 1) {
        printPortrait();
        dispatch(setSelectedBookletToken(undefined));
      } else {
        dispatch(setSelectedBookletToken(undefined));
      }
    });
  };

  const transformedHtml = useMemo(() => {
    if (!booklet?.content) return "";
    let i = 0;
    let html = booklet.content.replace(/\{\{musicianSignature\}\}/g, () => {
      return `<span data-sig-slot="true" data-idx="${i++}"></span>`;
    });
    html = html.replace(/\{\{musicianSignatureDate\}\}/g, () => {
      return `<span data-sig-date="true"></span>`;
    });
    return html;
  }, [booklet?.content]);

  const renderedContent = useMemo(() => {
    return parse(transformedHtml, {
      replace(node: any) {
        if (
          node.type === "tag" &&
          node.name === "span" &&
          node.attribs &&
          node.attribs["data-sig-slot"]
        ) {
          const idx = Number(node.attribs["data-idx"]);
          const isSigned = !!signedSlots[idx];
          return (
            <SignatureSlot
              key={`sig-slot-${idx}`}
              index={idx}
              signed={isSigned}
              signature={signature}
              onClick={() => handleApplySignature(idx)}
            />
          );
        }
        if (
          node.type === "tag" &&
          node.name === "span" &&
          node.attribs &&
          node.attribs["data-sig-date"]
        ) {
          return <SignatureDate value={formattedSignatureDate} />;
        }
        return undefined;
      },
    });
  }, [transformedHtml, signedSlots, signature]);

  const color = getContrastYIQ(mission?.CompanyColor ?? "#000000");

  return (
    <>
      <style>{`
        @keyframes sigPulse { 0% { box-shadow: 0 0 0 0 rgba(25,118,210,0.45);} 70% { box-shadow: 0 0 0 10px rgba(25,118,210,0);} 100% { box-shadow: 0 0 0 0 rgba(25,118,210,0);} }
        [data-sig-slot].sig-highlight { outline: 3px solid #1976d2; border-color: #1976d2 !important; animation: sigPulse 1.2s ease-out 2; border-style: solid !important; background: rgba(25,118,210,0.06); }
      `}</style>
      <DialogClose
        sx={{
          zIndex: 9999,
          m: "auto",
          maxWidth: 920,
          mt: 2,
          mb: 2,
        }}
        fullScreen
        open={Boolean(bookletToken)}
        onClose={() => dispatch(setSelectedBookletToken(undefined))}
      >
        {booklet?.active ? (
          <Sheet
            color="primary"
            variant="solid"
            sx={{
              p: 2,
              mb: 2,
              display: "flex",
              color,
              background: mission?.CompanyColor,
            }}
          >
            Please review and sign your contract for {mission?.CompanyName} -{" "}
            {mission?.SeasonName}
            <Box sx={{ flexGrow: 1 }} />
            <img
              src={mission?.CompanyLogo}
              style={{ height: 25, margin: "auto" }}
            />
          </Sheet>
        ) : (
          []
        )}
        <DialogContent>
          {booklet?.active ? (
            <>
              <div id="printableDiv">{renderedContent}</div>
            </>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                startDecorator={
                  <i className="fa-solid fa-triangle-exclamation"></i>
                }
              >
                Content not active yet
              </Typography>
              <Typography level="body-sm">Please check again later</Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 0 }}>
          {booklet?.locked ? (
            <Box sx={{ p: 1 }}>
              <Button
                onClick={print}
                startDecorator={<i className="fa-solid fa-print"></i>}
                size="sm"
                color="neutral"
              >
                Print
              </Button>
            </Box>
          ) : (
            []
          )}
          {!signature && !booklet?.locked ? (
            <Box sx={{ p: 2 }}>
              <Button
                fullWidth
                size="lg"
                onClick={() => {
                  if (mission?.onboardingRequired()) {
                    setOnboardingDialogOpen(true);
                    return;
                  }
                  setSignOpen(true);
                }}
              >
                Start Signing
              </Button>
            </Box>
          ) : signatureCount === 0 && !booklet?.locked ? (
            <Box sx={{ p: 2, display: "flex", gap: 1, width: "100%" }}>
              <Button fullWidth size="lg" onClick={handleSubmit}>
                Submit
              </Button>
            </Box>
          ) : (
            []
          )}
          {signature && signatureCount > 0 && !booklet?.locked ? (
            <Sheet
              variant="soft"
              color="primary"
              sx={{
                display: "flex",
                flex: 1,
                p: 2,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography color="primary" level="title-lg">
                {signatureCount} signature{signatureCount > 1 ? "s" : ""} left:
              </Typography>
              <Button onClick={() => jumpToNextSignature()}>
                Jump to next signature
              </Button>
            </Sheet>
          ) : (
            []
          )}
        </DialogActions>
      </DialogClose>
      <Dialog
        sx={{ zIndex: 10000 }}
        open={signOpen}
        onClose={() => setSignOpen(false)}
      >
        <DialogTitle>
          <Typography level="title-lg">Adopt your signature</Typography>
          <Typography level="body-sm">
            This signature will be used on spots you need to sign
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ width: 568 }}>
          <Signature
            onChange={(e) => {
              setSignature(e);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSignOpen(false);
              jumpToNextSignature();
            }}
            disabled={!signature}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {mission ? (
        <OnboardingRequiredDialog
          open={onboardingDialogOpen}
          onClose={() => setOnboardingDialogOpen(false)}
          mission={mission}
        />
      ) : null}
    </>
  );
}

export function Signature({
  onChange,
}: {
  onChange: (s?: string | null) => void;
}) {
  const ref: any = useRef();

  const handleChange = () => {
    const base64 = ref.current.toDataURL();
    const base64Data = base64.split(",")[1];

    onChange(base64Data);
  };

  return (
    <Box
      sx={{
        p: 0.5,
        textAlign: "left",
        width: "100%",
        maxWidth: 568,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 200,
          background: "#eceff1",
          border: "solid 1px #cfd8dc",
        }}
      >
        <Box
          sx={{
            top: 0,
            textAlign: "center",
            background: "#cfd8dc",
            borderRadius: 8,
          }}
        >
          <SignatureCanvas
            ref={ref}
            penColor="black"
            onEnd={handleChange}
            canvasProps={{
              width: 568,
              height: 200,
              className: "sigCanvas",
            }}
          />
        </Box>
        <Typography
          level="h1"
          sx={{
            position: "absolute",
            top: 70,
            zIndex: 100101,
            opacity: 0.1,
            pointerEvents: "none",
            margin: "auto",
            textAlign: "center",
            width: "100%",
          }}
        >
          Sign Here
        </Typography>
      </Box>
      <Chip
        variant="outlined"
        onClick={() => {
          ref.current.clear();
          onChange(null);
        }}
        sx={{ mt: 1 }}
      >
        Clear Signature
      </Chip>
    </Box>
  );
}

function SignatureSlot({
  index,
  signed,
  signature,
  onClick,
}: {
  index: number;
  signed: boolean;
  signature?: string | null;
  onClick: () => void;
}) {
  return (
    <Box
      data-sig-slot
      data-signed={signed ? "true" : undefined}
      data-idx={index}
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 220,
        minHeight: 60,
        px: 1.5,
        py: 1,
        border: "2px dashed",
        borderColor: signed ? "success.500" : "neutral.outlinedBorder",
        borderRadius: 8,
        cursor: "pointer",
        userSelect: "none",
        outline: "none",
        backgroundColor: signed ? "success.plainHoverBg" : "transparent",
      }}
    >
      {signed && signature ? (
        <Box
          component="img"
          src={`data:image/png;base64,${signature}`}
          alt="Signature"
          sx={{ maxHeight: 56, maxWidth: 300 }}
        />
      ) : (
        <Typography level="body-sm" sx={{ opacity: 0.7 }}>
          Click to sign
        </Typography>
      )}
    </Box>
  );
}

function SignatureDate({ value }: { value: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.75,
        py: 0.25,
        border: "1px dashed",
        borderColor: "neutral.outlinedBorder",
        borderRadius: 6,
        fontSize: "0.875rem",
        lineHeight: 1.6,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Box>
  );
}
